/* @flow */

import * as React from 'react';
import { memoize } from 'lodash/fp';
import styled from '@emotion/styled';

import Responsive from 'app/components/responsive/Responsive';
import Tappable from 'app/components/commons/Tappable';
import HeaderCell from './HeaderCell';
import PlayerPoolRow from './PlayerPoolRow';
import { getFactoredEntityPositionSalary } from '../../selectors/salaryExpenditureSelector';

type Props = {
  isRacing: boolean,
  onChange: string => void,
  onSort: (key: string, order: boolean) => void,
  players?: Array<*>,
  selectedPosition?: string,
  sortDescending?: boolean,
  sortField?: string,
  value?: string
};

const defaultProps = {
  players: [],
  selectedPosition: undefined
};

const Wrapper = styled.div(({ theme }) => ({
  width: '100%',
  [theme.mediaQuery.tabletLandscapeAndUp]: {
    borderCollapse: 'collapse',
    display: 'table',
    fontSize: '0.8rem'
  }
}));

const HeaderRow = styled.div({
  display: 'table-row'
});

const DummyHeaderCell = styled.div(({ theme }) => ({
  display: 'none',
  width: '2rem',
  [theme.mediaQuery.desktopAndUp]: {
    display: 'table-cell'
  }
}));

const Placeholder = styled.div({
  paddingLeft: '2rem'
});

const NameHeaderCell = styled(HeaderCell)({
  width: '56px'
});

class PlayerPool extends React.PureComponent<Props> {
  props: Props;

  static defaultProps = {
    sortDescending: undefined,
    sortField: undefined,
    value: undefined
  };

  handlePlayerChange = memoize((playerId: string) => () => {
    const { onChange } = this.props;
    onChange(playerId);
  });

  handleSort = (field: string, defaultDesc?: boolean = false) => () => {
    const { onSort, sortField, sortDescending } = this.props;

    onSort(field, field === sortField ? !sortDescending : defaultDesc);
  };

  render() {
    const { isRacing, selectedPosition, sortDescending, sortField, players, value } = this.props;

    if (players.length === 0) {
      return <Placeholder>No suitable players found.</Placeholder>;
    }

    return (
      <Wrapper>
        <Responsive tabletLandscape desktop>
          <HeaderRow>
            <DummyHeaderCell />
            {isRacing && (
              <Tappable
                component={HeaderCell}
                onTap={this.handleSort('number', true)}
                showSortKey={sortField === 'number'}
                sortDescending={sortDescending}
                style={{ width: 50, paddingLeft: '0.5em' }}
              >
                No
              </Tappable>
            )}
            <Tappable
              component={NameHeaderCell}
              onTap={this.handleSort(isRacing ? 'barrier' : 'name')}
              showSortKey={sortField === (isRacing ? 'barrier' : 'name')}
              sortDescending={sortDescending}
            >
              {isRacing ? 'Barrier' : 'Name'}
            </Tappable>
            {isRacing ? (
              <React.Fragment>
                <HeaderCell style={{ width: '140px' }} />
                <Tappable
                  component={HeaderCell}
                  onTap={this.handleSort('winProjected', true)}
                  showSortKey={sortField === 'winProjected'}
                  sortDescending={sortDescending}
                >
                  Win
                </Tappable>
                <Tappable
                  component={HeaderCell}
                  onTap={this.handleSort('placeProjected', true)}
                  showSortKey={sortField === 'placeProjected'}
                  sortDescending={sortDescending}
                >
                  Place
                </Tappable>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <HeaderCell />
                <Tappable
                  component={HeaderCell}
                  onTap={this.handleSort('average', true)}
                  showSortKey={sortField === 'average'}
                  sortDescending={sortDescending}
                >
                  Avg
                </Tappable>
                <Tappable
                  component={HeaderCell}
                  onTap={this.handleSort('form', true)}
                  showSortKey={sortField === 'form'}
                  sortDescending={sortDescending}
                >
                  Form
                </Tappable>
                <Tappable
                  component={HeaderCell}
                  onTap={this.handleSort('salary', true)}
                  showSortKey={sortField === 'salary'}
                  sortDescending={sortDescending}
                >
                  Salary
                </Tappable>
              </React.Fragment>
            )}
          </HeaderRow>
        </Responsive>
        {players.map(player => {
          const factoredSalary = getFactoredEntityPositionSalary(
            player.salary,
            player.positions,
            selectedPosition
          );

          return (
            <PlayerPoolRow
              key={player.id}
              average={player.average}
              awayTeam={player.awayAbbr}
              form={player.form}
              gameOrder={player.gameOrder}
              homeTeam={player.homeAbbr}
              image={player.imageURI}
              initials={player.initials}
              isHome={player.isHome}
              isRacing={isRacing}
              isScratched={player.isScratched}
              labels={player.labels}
              name={player.name}
              onClick={this.handlePlayerChange(player.id)}
              place={player.placeProjected}
              position={player.positionAbbr}
              reverseOrder={player.reverseOrder}
              rider={player.rider}
              riderImage={player.riderImage}
              salary={factoredSalary}
              selected={player.id === value}
              statusId={player.statusId}
              trainer={player.trainer}
              win={player.winProjected}
            />
          );
        })}
      </Wrapper>
    );
  }
}

PlayerPool.defaultProps = defaultProps;

export default PlayerPool;
